import React from 'react'
import Input from 'components/input'
import Button from 'components/button'
import Select from 'components/select'
import ColumnDescriptionField from 'components/spreadsheet/components/column/description-field'
import { ChevronDown } from 'components/icons'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { COLUMN_TYPE_NAMES, SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { isEditorEmpty } from 'components/spreadsheet/helpers/functions'
import { IColumnTypes } from 'components/spreadsheet/types'
import { EditorContent } from 'types'
import { SELECT_OPTIONS_MODES } from './useColumnTypePicker'
import { usePicker } from './context'
import Collapse, { CollapseGroup } from 'components/collapse'
import Field from 'components/field'
import SegmentedControl from 'components/segmented-control'
import ColumnValidationFields from 'components/spreadsheet/components/column/validation-fields'
import ColumnFormattingFields, {
  ColumnTypeFormattingFields
} from 'components/spreadsheet/components/column/formatting-fields'
import ColumnManualOptions from 'components/spreadsheet/components/column/manual-options'
import SelectSource from './select-source'

// Types that have no meaningful per-row default value in the add flow.
const NO_DEFAULT_VALUE_TYPES: IColumnTypes[] = ['attachment', 'vote']

// Multiselect defaults are stored as a JSON array string (matching Edit Column); parse it back to an
// array, tolerating anything malformed. Module-scoped (not a nested component function) both because it's
// pure and because the React Compiler can't yet handle a conditional expression inside a try/catch.
const parseMultiDefault = (value: unknown): string[] => {
  if (!value) return []
  try {
    const parsed = JSON.parse(String(value))
    if (Array.isArray(parsed)) return parsed as string[]
    return []
  } catch {
    return []
  }
}

// The add flow's manual list: the shared editor writing into the picker's in-memory draft, so nothing is
// persisted until the column is created.
const ManualOptions: React.FC = () => {
  const {
    optionsDraft,
    addOption,
    updateOption,
    removeOption,
    moveOption,
    sortOptions,
    expandOptionsViaCellEntry,
    setExpandOptionsViaCellEntry
  } = usePicker()

  return (
    <ColumnManualOptions
      options={optionsDraft}
      onAdd={addOption}
      onUpdate={updateOption}
      onRemove={removeOption}
      onMove={moveOption}
      onSort={sortOptions}
      expandOptionsViaCellEntry={expandOptionsViaCellEntry}
      onExpandOptionsChange={setExpandOptionsViaCellEntry}
    />
  )
}

// A select column's options: a manual list or sourced from a table, chosen via a segmented switcher
// (as in the mockups). Shown always-visible for select/multiselect since options are core to the type.
const SelectOptions: React.FC = () => {
  const { optionsMode, setOptionsMode } = usePicker()

  return (
    <SegmentedControl
      title="Select Options"
      ariaLabel="Where the options come from"
      options={SELECT_OPTIONS_MODES}
      value={optionsMode}
      onChange={setOptionsMode}
    >
      {optionsMode === 'manual' ? <ManualOptions /> : <SelectSource />}
    </SegmentedControl>
  )
}

// Stage 2 of the add-column flow: configure the column before creating it. Mirrors the Edit Column
// options relevant to the chosen type, but writes to an in-memory draft so nothing is persisted until
// the user submits — at which point the column is created in a single request.
const ColumnConfig: React.FC = () => {
  const { draft, updateDraft, back, submit, processing, nameInputRef, tablePublicId, optionsDraft } = usePicker()

  if (!draft) return null

  const kind = draft.kind
  const showDefault = !NO_DEFAULT_VALUE_TYPES.includes(kind)
  const isSelect = SELECT_COLUMN_TYPES.includes(kind)

  // Toggle an option in/out of the multiselect default (stored as a JSON array string).
  const toggleMultiDefault = (option: string): void => {
    const current = parseMultiDefault(draft.defaultValue)
    const updated = current.includes(option) ? current.filter((value) => value !== option) : [...current, option]
    updateDraft({ defaultValue: updated.length > 0 ? JSON.stringify(updated) : null })
  }

  return (
    <div className="flex flex-col max-h-[80vh]">
      <div className="flex items-center gap-2.5 p-2.5 border-b border-divider shrink-0">
        <Button
          internalType="transparent"
          type="button"
          aria-label="Back to types"
          className="h-auto flex items-center text-muted hover:text-body rounded p-0.5 -rotate-270"
          onClick={back}
        >
          <ChevronDown />
        </Button>
        <IconSelector kind={kind} />
        <span className="font-bold">{COLUMN_TYPE_NAMES[kind]}</span>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 whitespace-normal">
        <Field label="Name" htmlFor="new-column-name">
          <Input
            id="new-column-name"
            ref={nameInputRef}
            type="text"
            value={draft.name}
            onChange={(event) => updateDraft({ name: event.target.value })}
          />
        </Field>

        <ColumnDescriptionField
          description={draft.description}
          editorId="new-column-description"
          tablePublicId={tablePublicId}
          onChange={(content: EditorContent) => updateDraft({ description: isEditorEmpty(content) ? null : content })}
        />

        {/* No Type field here (the type is in the stage header), so the type's own settings sit where
            Edit Column puts them: after Name/Description, before Default Value. */}
        <ColumnTypeFormattingFields kind={kind} value={draft} onChange={updateDraft} />

        {showDefault && (
          <Field label="Default Value" htmlFor="new-column-default">
            {kind === 'select' ? (
              // Pull the default from the column's own manual options; with none configured there's
              // nothing to default to, so the picker is disabled and shows "No default".
              <Select
                inputId="new-column-default"
                options={optionsDraft.map((option) => ({ label: option, value: option }))}
                optionsSelected={draft.defaultValue ? [String(draft.defaultValue)] : []}
                onOptionClick={(value) => updateDraft({ defaultValue: value || null })}
                disabled={optionsDraft.length === 0}
                placeholder="No default"
                clearable
                searchable={false}
              />
            ) : kind === 'multiselect' ? (
              // Multiple defaults can be picked from the options; stored as a JSON array string.
              <Select
                inputId="new-column-default"
                options={optionsDraft.map((option) => ({ label: option, value: option }))}
                multiselect
                optionsSelected={parseMultiDefault(draft.defaultValue)}
                onOptionClick={toggleMultiDefault}
                disabled={optionsDraft.length === 0}
                placeholder="No default"
                searchable={false}
              />
            ) : (
              <Input
                id="new-column-default"
                type="text"
                value={draft.defaultValue == null ? '' : String(draft.defaultValue)}
                placeholder="No default"
                onChange={(event) => updateDraft({ defaultValue: event.target.value || null })}
              />
            )}
          </Field>
        )}

        {isSelect && <SelectOptions />}

        <CollapseGroup allowOne>
          <div className="mt-4">
            <Collapse text="Validation" headerClassName="py-2.5 px-2.5" bodyClassName="pt-1 pb-4">
              <ColumnValidationFields kind={kind} value={draft} onChange={updateDraft} />
            </Collapse>

            <Collapse text="Formatting" headerClassName="py-2.5 px-2.5" bodyClassName="pt-1 pb-4">
              <ColumnFormattingFields value={draft} onChange={updateDraft} />
            </Collapse>
          </div>
        </CollapseGroup>
      </div>

      <div className="flex gap-2 p-2.5 border-t border-divider shrink-0">
        <Button internalType="outline" className="h-auto py-2 px-3" onClick={back}>
          Cancel
        </Button>
        <Button internalType="primary" className="h-auto py-2 px-3 flex-1" isLoading={processing} onClick={submit}>
          Add column
        </Button>
      </div>
    </div>
  )
}

export default ColumnConfig
